import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2, AlertTriangle, Sparkles, ListFilter } from "lucide-react";
import { previewBreakdown, commitBreakdown } from "@/services/breakdownService";
import BreakdownPreviewTable from "./BreakdownPreviewTable";

const VALID_EXTS = [".pdf", ".docx", ".doc", ".fdx", ".fountain", ".txt", ".rtf"];

/**
 * Upload → preview → commit wizard for importing a finished shot-breakdown doc.
 * Preview rows are editable (enum corrections, world, screen-capture) before they
 * land as `pending` shots. Nothing is persisted until Commit.
 */
export default function ShotBreakdownImportWizard({ open, onOpenChange, projectId, onCommitted }) {
  const [step, setStep] = useState("upload"); // upload | preview
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const reset = () => {
    setStep("upload"); setPreview(null); setRows([]); setIssuesOnly(false);
    setUploading(false); setCommitting(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const processFile = useCallback(async (file) => {
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum file size is 50MB" });
      return;
    }
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!VALID_EXTS.includes(ext)) {
      toast.error("Unsupported format", { description: "Supported: PDF, DOCX, Final Draft, Fountain, TXT, RTF" });
      return;
    }
    setUploading(true);
    const toastId = toast.loading(`Parsing ${file.name}…`);
    try {
      const data = await previewBreakdown(file, projectId);
      setPreview(data);
      setRows(data.shot_rows || []);
      setStep("preview");
      toast.success(`Parsed ${data.shot_count} shots across ${data.scenes?.length || 0} scenes`, { id: toastId });
    } catch (err) {
      toast.error("Could not parse breakdown", {
        id: toastId,
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setUploading(false);
    }
  }, [projectId]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false); dragCounter.current = 0;
    const f = e.dataTransfer?.files?.[0];
    if (f) processFile(f);
  }, [processFile]);

  const onFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) processFile(f);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const patchRow = (idx, newRow) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? newRow : r)));
  };

  const lowCount = rows.filter((r) => r.confidence === "low").length;
  const screenCount = rows.filter((r) => r.render_kind === "screen_capture").length;

  const handleCommit = async () => {
    setCommitting(true);
    try {
      const res = await commitBreakdown({
        project_id: projectId,
        shot_rows: rows,
        scenes: preview.scenes,
        expansion_hints: preview.expansion_hints,
        creative_direction: preview.creative_direction,
        constraints: preview.constraints,
        world_segments: preview.world_segments,
        document_title: preview.document_title,
        source_filename: preview.source_filename,
        source_format: preview.source_format,
        page_count: preview.page_count,
      });
      toast.success(`Imported ${res.anchor_count} shots across ${res.scene_numbers.length} scenes`);
      handleClose(false);
      onCommitted?.(res);
    } catch (err) {
      toast.error("Import failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-4xl max-h-[88vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg text-[var(--text-primary)] flex items-center gap-2">
            <Sparkles size={18} className="text-[var(--color-gold-400)]" />
            Import Shot Breakdown
          </DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div
            onDragEnter={(e) => { e.preventDefault(); dragCounter.current++; if (e.dataTransfer?.types?.includes("Files")) setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className={`m-2 rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-16 px-6 text-center transition-colors ${
              isDragging ? "border-[var(--color-gold-500)] bg-[rgba(212,168,83,0.05)]" : "border-[var(--border-default)]"
            }`}
          >
            {uploading ? (
              <>
                <Loader2 size={32} className="animate-spin text-[var(--color-gold-400)] mb-3" />
                <p className="text-sm text-[var(--text-secondary)]">Parsing & extracting the shot list…</p>
              </>
            ) : (
              <>
                <UploadCloud size={36} className="text-[var(--text-muted)] mb-3" />
                <p className="text-sm font-medium text-[var(--text-primary)] mb-1">Drop a shot-breakdown document</p>
                <p className="text-xs text-[var(--text-muted)] mb-4">PDF, DOCX, Final Draft, Fountain, TXT, RTF · up to 50MB</p>
                <Button className="btn-primary" onClick={() => fileInputRef.current?.click()}>
                  <FileText size={14} className="mr-1.5" /> Choose file
                </Button>
                <input ref={fileInputRef} type="file" accept={VALID_EXTS.join(",")} onChange={onFileSelect} className="hidden" />
              </>
            )}
          </div>
        )}

        {step === "preview" && preview && (
          <>
            {/* summary + warnings */}
            <div className="flex flex-wrap items-center gap-2 px-2 text-xs">
              <span className="text-[var(--text-secondary)]">{rows.length} shots · {preview.scenes?.length || 0} scenes</span>
              {screenCount > 0 && (
                <span className="text-emerald-400">{screenCount} screen/UI</span>
              )}
              {lowCount > 0 && (
                <span className="text-amber-400 flex items-center gap-1">
                  <AlertTriangle size={11} /> {lowCount} need review
                </span>
              )}
              <button
                onClick={() => setIssuesOnly((v) => !v)}
                className={`ml-auto text-[11px] px-2 py-1 rounded border flex items-center gap-1 ${
                  issuesOnly ? "border-amber-500/40 text-amber-300 bg-amber-500/10" : "border-[var(--border-subtle)] text-[var(--text-muted)]"
                }`}
              >
                <ListFilter size={11} /> {issuesOnly ? "Showing issues" : "Only issues"}
              </button>
            </div>

            {(preview.warnings || []).length > 0 && (
              <div className="mx-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-300">
                {preview.warnings.map((w, i) => <p key={i}>{w}</p>)}
              </div>
            )}

            {/* editable table */}
            <div className="flex-1 overflow-y-auto px-2 py-1 min-h-0">
              <BreakdownPreviewTable
                rows={rows}
                scenes={preview.scenes}
                onRowChange={patchRow}
                issuesOnly={issuesOnly}
              />
            </div>

            {/* footer */}
            <div className="flex items-center gap-2 pt-2 px-2 border-t border-[var(--border-subtle)]">
              <Button variant="ghost" onClick={() => setStep("upload")} disabled={committing}>Back</Button>
              <p className="text-[10px] text-[var(--text-muted)] flex-1">
                Shots import as <b>pending</b> — render them from the Storyboard tab, or expand beats afterwards.
              </p>
              <Button className="btn-primary" onClick={handleCommit} disabled={committing}>
                {committing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
                Import {rows.length} shots
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
